import API_BASE from '../../utils/api';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-800',
  principal: 'bg-red-100 text-red-800',
  class_teacher: 'bg-green-100 text-green-800',
  parent: 'bg-blue-100 text-blue-800',
  super_admin: 'bg-gray-100 text-gray-800',
};

const ACTION_COLORS = {
  LOGIN: 'bg-emerald-100 text-emerald-700',
  CREATE_USER: 'bg-blue-100 text-blue-700',
  UPDATE_USER: 'bg-amber-100 text-amber-700',
  DELETE_USER: 'bg-red-100 text-red-700',
  DEFAULT: 'bg-gray-100 text-gray-700',
};

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);

  const [filters, setFilters] = useState({
    role: '', module: '', search: '', startDate: '', endDate: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const fetchModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/activity-logs/modules`);
      setModules(res.data || []);
    } catch (_) {}
  }, []);

  const fetchLogs = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (filters.role) params.append('role', filters.role);
      if (filters.module) params.append('module', filters.module);
      if (filters.search) params.append('search', filters.search);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const res = await axios.get(`${API_BASE}/activity-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (_) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchModules(); }, [fetchModules]);
  useEffect(() => { setPage(1); fetchLogs(1); }, [filters]);
  useEffect(() => { fetchLogs(page); }, [page]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toISODate = (d) => d.toISOString().slice(0, 10);

  const applyDatePreset = (preset) => {
    const today = new Date();
    if (preset === 'today') {
      const iso = toISODate(today);
      setFilters(prev => ({ ...prev, startDate: iso, endDate: iso }));
    } else if (preset === '7days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      setFilters(prev => ({ ...prev, startDate: toISODate(start), endDate: toISODate(today) }));
    } else if (preset === '30days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      setFilters(prev => ({ ...prev, startDate: toISODate(start), endDate: toISODate(today) }));
    } else if (preset === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setFilters(prev => ({ ...prev, startDate: toISODate(start), endDate: toISODate(today) }));
    }
  };

  const activeFilterChips = [
    filters.search && { key: 'search', label: `"${filters.search}"` },
    filters.role && { key: 'role', label: `Role: ${filters.role}` },
    filters.module && { key: 'module', label: `Module: ${filters.module}` },
    filters.startDate && { key: 'startDate', label: `From: ${filters.startDate}` },
    filters.endDate && { key: 'endDate', label: `To: ${filters.endDate}` },
  ].filter(Boolean);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const actionColor = (action) => ACTION_COLORS[action] || ACTION_COLORS.DEFAULT;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Activity Log</h1>
              <p className="text-sm text-gray-500">Track every action performed across the system</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6 space-y-4">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow px-6 py-4 flex items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-800 text-lg">{total.toLocaleString()}</span>
            <span>total activities recorded</span>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                🔍 Filters
                {activeFilterChips.length > 0 && (
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full px-2 py-0.5">
                    {activeFilterChips.length}
                  </span>
                )}
              </h3>
              {activeFilterChips.length > 0 && (
                <button
                  onClick={() => setFilters({ role: '', module: '', search: '', startDate: '', endDate: '' })}
                  className="text-xs text-indigo-600 hover:underline font-medium"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="col-span-1 md:col-span-1 lg:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search user, action, details..."
                  value={filters.search}
                  onChange={e => handleFilterChange('search', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                <select
                  value={filters.role}
                  onChange={e => handleFilterChange('role', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="principal">Principal</option>
                  <option value="class_teacher">Class Teacher</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Module</label>
                <select
                  value={filters.module}
                  onChange={e => handleFilterChange('module', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Modules</option>
                  {modules.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Date Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={e => handleFilterChange('startDate', e.target.value)}
                    className="border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 min-w-0"
                  />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={e => handleFilterChange('endDate', e.target.value)}
                    className="border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 min-w-0"
                  />
                </div>
              </div>
            </div>

            {/* Quick date presets */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span className="text-xs text-gray-400">Quick range:</span>
              {[
                { id: 'today', label: 'Today' },
                { id: '7days', label: 'Last 7 days' },
                { id: '30days', label: 'Last 30 days' },
                { id: 'month', label: 'This month' },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => applyDatePreset(p.id)}
                  className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition"
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Active filter chips */}
            {activeFilterChips.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap pt-1 border-t">
                {activeFilterChips.map(chip => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-medium px-2.5 py-1 rounded-full mt-2"
                  >
                    {chip.label}
                    <button
                      onClick={() => handleFilterChange(chip.key, '')}
                      className="text-indigo-400 hover:text-indigo-700 leading-none"
                      aria-label={`Remove ${chip.key} filter`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Log Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center">
                        <div className="flex justify-center items-center gap-2 text-gray-500">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                          Loading...
                        </div>
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                        <div className="text-3xl mb-2">📋</div>
                        <p>No activity found</p>
                      </td>
                    </tr>
                  ) : logs.map(log => (
                    <tr key={log._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(log.createdAt)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{log.userName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[log.role] || 'bg-gray-100 text-gray-700'}`}>
                          {log.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${actionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{log.module}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={log.details}>{log.details || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{log.ip || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t flex items-center justify-between bg-gray-50">
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages} &mdash; {total} records
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-40 hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-40 hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
